// Thanh soạn thảo cho ô nội dung bài Kênh Cộng Đồng (admin + CTV):
// đậm/nghiêng/gạch ngang, tiêu đề, gạch đầu dòng, danh sách số, link có chữ
// hiển thị, kẻ ngang, chèn ảnh của bài vào giữa nội dung.
//
// Cú pháp sinh ra phải khớp renderer web (frontend/.../community-content-blocks.js
// + community-linkified-text.js) và app (mobile/.../community-content-blocks.ts).
// Form đăng bài của user (frontend) vẫn chỉ có B/I — đọc thì hiểu hết cú pháp.
import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { Button, Dropdown, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBold,
    faItalic,
    faStrikethrough,
    faHeading,
    faListUl,
    faListOl,
    faLink,
    faMinus,
    faImage,
} from "@fortawesome/free-solid-svg-icons";
import { buildMarkdownInsert, getMarkdownShortcutMarker } from "./community-markdown-insert";
import {
    buildLinePrefixInsert,
    buildDividerInsert,
    buildImageInsert,
    buildLinkInsert,
    HEADING_PREFIX_REGEX,
    BULLET_PREFIX_REGEX,
    NUMBERED_PREFIX_REGEX,
} from "./community-block-insert";

/**
 * Lấy DOM <textarea> thật từ ref của antd Input.TextArea (antd v5 bọc trong
 * resizableTextArea), fallback về chính ref nếu là textarea thuần.
 */
function resolveTextarea(ref) {
    const node = ref?.current;
    if (!node) return null;
    return node.resizableTextArea?.textArea || (node.tagName === "TEXTAREA" ? node : null);
}

/**
 * @param {object} props
 * @param {object} props.textareaRef ref tới Input.TextArea của form
 * @param {(value: string) => void} props.onChange ghi value mới vào form
 * @param {string} props.value nội dung hiện tại — dùng để biết lúc nào React đã
 *   commit xong để khôi phục vùng bôi đen
 * @param {number} props.imageCount số ảnh đang đính kèm bài (để chèn `![N]`)
 */
export default function CommunityMarkdownToolbar({ textareaRef, onChange, value = "", imageCount = 0 }) {
    // Vùng chọn cần đặt lại sau khi form nhận value mới
    const pendingSelectionRef = useRef(null);

    /**
     * Áp một "mô tả chỉnh sửa" {from, to, text, selectionStart, selectionEnd}.
     * Chèn qua execCommand để trình duyệt ghi vào undo stack — Ctrl+Z hoàn tác
     * được cả thao tác trên thanh công cụ (góp ý 29/07). Set value programmatic
     * (nhánh fallback, vd Firefox) sẽ mất undo cho lần chèn đó.
     */
    const applyEdit = useCallback(
        (edit) => {
            const el = resolveTextarea(textareaRef);
            if (!el || !edit) return;
            el.focus();
            el.setSelectionRange(edit.from, edit.to);
            const inserted = document.execCommand?.("insertText", false, edit.text);
            if (!inserted) {
                const next = `${el.value.slice(0, edit.from)}${edit.text}${el.value.slice(edit.to)}`;
                onChange(next);
            }
            pendingSelectionRef.current = edit;
        },
        [textareaRef, onChange]
    );

    /** Thao tác dựng edit từ (value, start, end) hiện tại của textarea */
    const runEdit = useCallback(
        (build) => {
            const el = resolveTextarea(textareaRef);
            if (!el) return;
            applyEdit(build(el.value, el.selectionStart, el.selectionEnd));
        },
        [textareaRef, applyEdit]
    );

    // Bọc cặp dấu (B/I/S) — buildMarkdownInsert trả về cả value nên quy về
    // dạng edit descriptor bằng cách thay đúng đoạn [from, to).
    const insertMarker = useCallback(
        (marker) => {
            const el = resolveTextarea(textareaRef);
            if (!el) return;
            const from = el.selectionStart;
            const to = el.selectionEnd;
            const next = buildMarkdownInsert(el.value, from, to, marker);
            const suffixLen = el.value.length - to;
            applyEdit({
                from,
                to,
                text: next.value.slice(from, next.value.length - suffixLen),
                selectionStart: next.selectionStart,
                selectionEnd: next.selectionEnd,
            });
        },
        [textareaRef, applyEdit]
    );

    // Khôi phục vùng chọn ngay sau khi textarea nhận value mới. Dùng
    // useLayoutEffect thay cho requestAnimationFrame vì rAF có thể chạy trước
    // lúc React commit → trình duyệt đẩy con trỏ về cuối, mất vị trí.
    useLayoutEffect(() => {
        const pending = pendingSelectionRef.current;
        const el = resolveTextarea(textareaRef);
        if (!pending || !el) return;
        pendingSelectionRef.current = null;
        el.focus();
        el.setSelectionRange(pending.selectionStart, pending.selectionEnd);
    }, [value, textareaRef]);

    // Phím tắt Ctrl/Cmd + B / I gắn thẳng vào textarea (antd không cho truyền
    // onKeyDown xuyên Form.Item một cách chắc chắn nên nghe trực tiếp trên DOM)
    useEffect(() => {
        const el = resolveTextarea(textareaRef);
        if (!el) return undefined;
        const handleKeyDown = (e) => {
            const marker = getMarkdownShortcutMarker(e);
            if (!marker) return;
            e.preventDefault();
            insertMarker(marker);
        };
        el.addEventListener("keydown", handleKeyDown);
        return () => el.removeEventListener("keydown", handleKeyDown);
    }, [textareaRef, insertMarker]);

    // onMouseDown preventDefault: giữ focus + vùng bôi đen của textarea khi bấm
    // nút (bấm nút làm textarea blur là nguyên nhân "bấm B không ăn").
    const keepSelection = (e) => e.preventDefault();

    const imageMenu = {
        items: Array.from({ length: imageCount }, (_, i) => ({
            key: String(i + 1),
            label: `Ảnh ${i + 1}`,
        })),
        onClick: ({ key }) => runEdit((v, s, e) => buildImageInsert(v, s, e, Number(key))),
    };

    const buttons = [
        { title: "In đậm (Ctrl/Cmd + B)", icon: faBold, onClick: () => insertMarker("**") },
        { title: "In nghiêng (Ctrl/Cmd + I)", icon: faItalic, onClick: () => insertMarker("*") },
        { title: "Gạch ngang chữ", icon: faStrikethrough, onClick: () => insertMarker("~~") },
        {
            title: "Tiêu đề mục",
            icon: faHeading,
            onClick: () => runEdit((v, s, e) => buildLinePrefixInsert(v, s, e, () => "## ", HEADING_PREFIX_REGEX)),
        },
        {
            title: "Gạch đầu dòng",
            icon: faListUl,
            onClick: () => runEdit((v, s, e) => buildLinePrefixInsert(v, s, e, () => "- ", BULLET_PREFIX_REGEX)),
        },
        {
            title: "Danh sách đánh số",
            icon: faListOl,
            onClick: () =>
                runEdit((v, s, e) => buildLinePrefixInsert(v, s, e, (i) => `${i + 1}. `, NUMBERED_PREFIX_REGEX)),
        },
        {
            title: "Chèn link có chữ hiển thị",
            icon: faLink,
            onClick: () => runEdit(buildLinkInsert),
        },
        { title: "Đường kẻ ngang", icon: faMinus, onClick: () => runEdit(buildDividerInsert) },
    ];

    return (
        <div className="flex flex-wrap items-center gap-2 mb-2">
            {buttons.map(({ title, icon, onClick }) => (
                <Tooltip key={title} title={title}>
                    <Button
                        size="small"
                        onMouseDown={keepSelection}
                        onClick={onClick}
                        icon={<FontAwesomeIcon icon={icon} />}
                    />
                </Tooltip>
            ))}

            <Tooltip title={imageCount ? "Chèn ảnh của bài vào vị trí con trỏ" : "Thêm ảnh cho bài trước đã"}>
                <Dropdown menu={imageMenu} trigger={["click"]} disabled={!imageCount}>
                    <Button size="small" onMouseDown={keepSelection} icon={<FontAwesomeIcon icon={faImage} />} />
                </Dropdown>
            </Tooltip>

            <span className="text-xs text-content-3">Bôi đen chữ rồi bấm nút (Ctrl+B, Ctrl+I)</span>
        </div>
    );
}
